/**
 * User-shell navigation, mirroring the legacy top navbar + footer
 * (`frontend/src/containers/layout/*`, PAGE_MAP.md "Navigation structure").
 * Centralized here so a locale layer can wrap the labels later (cross-cutting
 * i18n note) and so the header/footer stay in sync. Paths are preserved 1:1.
 */
export interface NavItem {
  label: string;
  href: string;
}

/**
 * Primary links in the top navbar, in the exact order of the legacy top nav:
 * Devices, Jobs, Docs, then the "My account" dropdown (rendered separately in
 * the header between Docs and Marketplace), then Marketplace last.
 */
export const primaryNav: NavItem[] = [
  { label: "Devices", href: "/devices" },
  { label: "Jobs", href: "/jobs" },
  { label: "Docs", href: "/docs" },
  { label: "Marketplace", href: "/devices/marketplace" },
];

/**
 * The account/worker toolbar restored from the legacy `components/tabs.tsx`
 * chip row, in its exact order and link set. Rendered as `AccountTabsNav`.
 *
 * Deliberate departures from legacy: the "Availabel Jobs" typo is fixed, and
 * Labelling / My offers stay out (legacy has them commented out of the row).
 * Note this is NOT the same set as `accountNav` — that dropdown carries both.
 */
export const accountTabsNav: NavItem[] = [
  { label: "My account", href: "/myaccount" },
  { label: "My Payments", href: "/mypayments" },
  { label: "Deposit Funds", href: "/deposit" },
  { label: "Automations", href: "/automations" },
  { label: "Withdraw Funds", href: "/withdraw" },
  { label: "Support", href: "/mysupport" },
  { label: "Refer a Friend", href: "/invite" },
  { label: "Edit Profile", href: "/myprofile" },
  { label: "Submit a Job", href: "/job_create" },
  { label: "My Jobs", href: "/myjobs" },
  { label: "My Workers", href: "/myworkers" },
  { label: "Send Bonus", href: "/send_money" },
  { label: "Available Jobs", href: "/jobs" },
  { label: "Finished Jobs", href: "/jobs_finished" },
  { label: "Job Invitations", href: "/jobs_invited" },
  { label: "History", href: "/history" },
  { label: "API", href: "/developers" },
];

/** A labelled section of the account dropdown. */
export interface NavGroup {
  /** Section heading; omitted for the top (general) group. */
  label?: string;
  items: NavItem[];
}

/**
 * Items in the "My account" dropdown / mobile menu, grouped exactly like the
 * legacy `components/nav_dropdown` (General / Employer / Worker sections).
 *
 * This is a superset of `accountTabsNav` (it also carries Labelling and My
 * offers). Legacy renders BOTH the dropdown and the `tabs.tsx` chip row, so the
 * two coexisting is the intended parity behaviour, not a duplication bug.
 */
export const accountNav: NavGroup[] = [
  {
    items: [
      { label: "My account", href: "/myaccount" },
      { label: "My Payments", href: "/mypayments" },
      { label: "Deposit Funds", href: "/deposit" },
      { label: "Withdraw Funds", href: "/withdraw" },
      { label: "Automations", href: "/automations" },
      { label: "Support", href: "/mysupport" },
      { label: "Refer a Friend", href: "/invite" },
      // Legacy puts MESSAGES here, between Refer a Friend and Edit Profile, and
      // this dropdown is the *only* entry point to chat in the legacy navbar.
      { label: "Messages", href: "/chat" },
      { label: "Edit Profile", href: "/myprofile" },
      { label: "Labelling", href: "/labelling" },
      { label: "History", href: "/history" },
      { label: "API", href: "/developers" },
    ],
  },
  {
    label: "Employer",
    items: [
      { label: "Submit a Job", href: "/job_create" },
      { label: "My Jobs", href: "/myjobs" },
      { label: "My Workers", href: "/myworkers" },
      { label: "Send Bonus", href: "/send_money" },
    ],
  },
  {
    label: "Worker",
    items: [
      { label: "Available Jobs", href: "/jobs" },
      { label: "Finished Jobs", href: "/jobs_finished" },
      { label: "Job Invitations", href: "/jobs_invited" },
      { label: "My offers", href: "/myoffers" },
    ],
  },
];
